import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { proService } from '../../services';
import { ClubPanel, PanelHead, PillButton, RingTab, TagPill, ErrorBand, EmptyBlock, INK, MUTED, SUBTLE, INSET, RULE } from '../club/clubUi';
import { money } from './vendor/vendorUi';
import { bookingWhen, offerSessions } from '../../utils/proBookingTime';
import { FILTERS, STAGE_PILL, bookingsView, needsAction, stageOf } from './bookingsModel';

const EMPTY = {
  upcoming: ['NOTHING COMING UP', 'Offers and paid bookings with a session still ahead land here.'],
  action: ['NOTHING WAITING ON YOU', 'New offers, counters on your turn and disputes show up here.'],
  past: ['NOTHING PAST YET', 'Finished, declined and expired bookings collect here.'],
  all: ['NO BOOKINGS YET', 'When someone makes an offer on your page, it shows up here.'],
};

/**
 * BOOKINGS on /pro/dashboard: every offer and booking this page has received, from the same
 * `GET pro/offers/incoming` the booking desk reads (it covers every page the account owns, so the
 * list is narrowed to the page being managed). Who booked, the service, every session's date and
 * time, the amount and where it stands; each row opens the offer.
 */
export default function BookingsPanel({ pageId, phone = false }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('upcoming');

  const load = useCallback(async () => {
    setError('');
    const r = await proService.incomingOffers();
    if (r.success) setList((r.data || []).filter((o) => !pageId || o.businessId === pageId));
    else { setList(null); setError(r.error || 'Could not load your bookings.'); }
  }, [pageId]);
  useEffect(() => { setList(null); load(); }, [load]);

  const view = useMemo(() => (list ? bookingsView(list, filter) : null), [list, filter]);

  return (
    <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="bookings-panel">
      <PanelHead title="BOOKINGS" right={phone ? null : 'BY NEXT SESSION'} />
      <div className="flex" role="group" aria-label="Show bookings" style={{ gap: 6, flexWrap: phone ? 'nowrap' : 'wrap', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {FILTERS.map(([k, l]) => (
          <RingTab key={k} on={filter === k} off={INSET} height={36} fontSize={10} onClick={() => setFilter(k)} badge={view ? view.counts[k] : null} data-testid={`bookings-filter-${k}`}>{l}</RingTab>
        ))}
      </div>

      {error ? (
        <>
          <ErrorBand title="COULD NOT LOAD" testId="bookings-error">{error}</ErrorBand>
          <span><PillButton variant="secondary" size={40} onClick={load}>TRY AGAIN</PillButton></span>
        </>
      ) : !view ? (
        <div className="py-10 text-center" data-testid="bookings-loading"><LoadingSpinner label="LOADING BOOKINGS…" /></div>
      ) : view.rows.length === 0 ? (
        <EmptyBlock title={EMPTY[filter][0]} body={EMPTY[filter][1]} testId="bookings-empty" />
      ) : (
        <div className="flex flex-col" data-testid="bookings-list">
          {view.rows.map((o, i) => <BookingRow key={o.id} o={o} first={i === 0} phone={phone} />)}
        </div>
      )}
    </ClubPanel>
  );
}

function BookingRow({ o, first, phone }) {
  const stage = stageOf(o);
  const [label, bg, fg] = STAGE_PILL[stage] || [String(stage).toUpperCase(), INSET, INK];
  const sessions = offerSessions(o);
  const now = new Date();
  const who = o.buyerName || o.buyerEmail || 'Someone';
  return (
    <Link to={`/pro/offer/${o.id}`} className="flex font-mono hover:opacity-70" style={{ gap: 14, padding: phone ? '14px 0' : '16px 4px', borderTop: first ? 'none' : RULE, color: INK, textDecoration: 'none', transition: 'opacity .2s', flexDirection: phone ? 'column' : 'row' }} data-testid={`booking-row-${o.id}`}>
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 5 }}>
        <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{who}</span>
          {needsAction(o) && <span className="rounded-full" style={{ width: 8, height: 8, background: '#FF00AA' }} aria-label="Needs you" />}
        </span>
        {o.buyerName && o.buyerEmail && <span className="truncate" style={{ fontSize: 11, color: MUTED }}>{o.buyerEmail}</span>}
        {o.serviceName && <span style={{ fontSize: 12, fontWeight: 700 }}>{o.serviceName}{o.sessionCount > 1 ? ` · ${o.sessionCount} SESSIONS` : ''}</span>}
        {sessions.length === 0 ? (
          <span style={{ fontSize: 12, color: SUBTLE }}>No date set</span>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0" style={{ gap: 2 }}>
            {sessions.map((s, k) => {
              const done = new Date(s.endsAt || s.startsAt || s.eventDate) < now;
              return (
                <li key={s.number || k} style={{ fontSize: 12, color: done ? SUBTLE : INK }}>
                  {sessions.length > 1 && <span style={{ fontWeight: 800 }}>{s.number || k + 1}. </span>}
                  {bookingWhen(s)}
                </li>
              );
            })}
          </ul>
        )}
      </span>
      <span className="flex flex-shrink-0" style={{ gap: 8, flexDirection: phone ? 'row' : 'column', alignItems: phone ? 'center' : 'flex-end', justifyContent: phone ? 'space-between' : 'flex-start' }}>
        <span style={{ fontSize: 16, fontWeight: 800 }}>{money(o.currentAmountCents || 0)}</span>
        <TagPill bg={bg} fg={fg} size={8} padding="4px 10px" data-testid={`booking-status-${o.id}`}>{label}</TagPill>
      </span>
    </Link>
  );
}
